import { useEffect, useRef, useState } from 'react';
import { text, zones, type Locale } from '../shared/content';
import type { AdBusinessOverview } from '../server/ad-business';
import AdAssetUpload from './AdAssetUpload';
import { api, post } from './api';
import { errorText } from './i18n';

const copy = {
  title: text('Advertising business', '广告业务与审核', '廣告業務與審核', 'Negocio y revisión publicitaria'),
  private: text(
    'Sponsor contacts and contract notes are private to advertising operators and platform administrators.',
    '赞助商联系人及合同备注仅广告运营和平台管理员可见。',
    '贊助商聯絡人及合約備註僅廣告營運和平台管理員可見。',
    'Los contactos y las notas contractuales son privados para operadores publicitarios y administradores.',
  ),
  sponsors: text('Sponsors', '赞助商', '贊助商', 'Patrocinadores'),
  campaigns: text('Campaigns', '投放与版本', '投放與版本', 'Campañas y versiones'),
  placements: text('Placements', '广告位', '廣告位', 'Ubicaciones'),
  company: text('Company', '公司名称', '公司名稱', 'Empresa'),
  display: text('Public sponsor label', '公开赞助名称', '公開贊助名稱', 'Nombre público'),
  contact: text('Contact person', '联系人', '聯絡人', 'Persona de contacto'),
  email: text('Contact email', '联系邮箱', '聯絡信箱', 'Correo de contacto'),
  notes: text(
    'Private contract notes',
    '合同备注（私有）',
    '合約備註（私有）',
    'Notas contractuales privadas',
  ),
  industry: text('Industry', '行业', '產業', 'Sector'),
  saveSponsor: text(
    'Save sponsor for review',
    '保存赞助商并待审',
    '儲存贊助商並待審',
    'Guardar para revisión',
  ),
  newSponsor: text('New sponsor', '新建赞助商', '新增贊助商', 'Nuevo patrocinador'),
  newCampaign: text('New campaign', '新建投放', '新增投放', 'Nueva campaña'),
  edit: text('Edit', '编辑', '編輯', 'Editar'),
  cancel: text('Cancel editing', '取消编辑', '取消編輯', 'Cancelar edición'),
  sponsor: text('Sponsor record', '赞助商记录', '贊助商紀錄', 'Registro del patrocinador'),
  placement: text('Placement', '广告位', '廣告位', 'Ubicación'),
  headline: text('Headline', '标题', '標題', 'Título'),
  body: text('Message', '正文', '內文', 'Mensaje'),
  url: text('HTTPS destination', 'HTTPS 目标网址', 'HTTPS 目標網址', 'Destino HTTPS'),
  starts: text('Starts · UTC', '开始时间 · UTC', '開始時間 · UTC', 'Inicio · UTC'),
  ends: text('Ends · UTC', '结束时间 · UTC', '結束時間 · UTC', 'Fin · UTC'),
  image: text('Optional campaign image', '广告图片（可选）', '廣告圖片（選填）', 'Imagen opcional'),
  assetId: text('Selected image asset ID', '已选图片素材 ID', '已選圖片素材 ID', 'ID de imagen seleccionada'),
  alt: text(
    'Image alternative text · required with an image',
    '图片替代文字（有图必填）',
    '圖片替代文字（有圖必填）',
    'Texto alternativo · obligatorio con imagen',
  ),
  removeImage: text('Use text only', '使用纯文字', '使用純文字', 'Usar solo texto'),
  imageInfo: text(
    'Text-only campaigns are supported. Uploading or editing creates a new version; it does not approve an image for public delivery.',
    '支持纯文字广告。上传或编辑会产生新版本，不会自动批准图片公开投放。',
    '支援純文字廣告。上傳或編輯會產生新版本，不會自動批准圖片公開投放。',
    'Se admiten anuncios de texto. Subir o editar crea una versión nueva; no autoriza su publicación.',
  ),
  project: text(
    'Public weekly community project',
    '公开周社区项目',
    '公開每週社區專案',
    'Proyecto comunitario semanal público',
  ),
  projectInfo: text(
    'Community campaigns bind one public project and must fit its week. Private two-person cooperation jobs are not eligible.',
    '社区位须绑定单个公开项目，排期须在其周活动内；不能绑定私人双人协作。',
    '社區位須綁定單一公開專案，排程須在該週活動內；不能綁定私人雙人協作。',
    'La ubicación comunitaria exige un proyecto público dentro de su semana. No admite tareas privadas de cooperación.',
  ),
  choose: text('Choose…', '请选择…', '請選擇…', 'Elegir…'),
  draft: text('Draft', '草稿', '草稿', 'Borrador'),
  pending: text('Pending review', '待审核', '待審核', 'Pendiente'),
  approved: text('Approved', '已批准', '已批准', 'Aprobado'),
  paused: text('Paused', '已暂停', '已暫停', 'Pausado'),
  rejected: text('Rejected', '已拒绝', '已拒絕', 'Rechazado'),
  expired: text('Expired', '已到期', '已到期', 'Finalizado'),
  saveDraft: text(
    'Save new draft version',
    '保存新草稿版本',
    '儲存新草稿版本',
    'Guardar nueva versión borrador',
  ),
  submit: text('Save and submit for review', '保存并提交审核', '儲存並送審', 'Guardar y enviar a revisión'),
  approvalInfo: text(
    'Every save invalidates the previous approval. Review the displayed version before approving; sponsor approval and an enabled placement are also required.',
    '每次保存均使旧批准失效。审批须核对当前展示版本，同时要求赞助商已审核、广告位已启用。',
    '每次儲存均使舊批准失效。審批須核對目前展示版本，同時要求贊助商已審核、廣告位已啟用。',
    'Cada guardado invalida la aprobación anterior. Revisa esta versión; el patrocinador debe estar aprobado y la ubicación habilitada.',
  ),
  revision: text('Version', '版本', '版本', 'Versión'),
  reviewNote: text(
    'Review reason · at least 3 characters',
    '审核说明（至少 3 字）',
    '審核說明（至少 3 字）',
    'Motivo de revisión · mínimo 3 caracteres',
  ),
  approve: text('Approve this version', '批准此版本', '批准此版本', 'Aprobar esta versión'),
  pause: text('Pause', '暂停', '暫停', 'Pausar'),
  reject: text('Reject', '拒绝', '拒絕', 'Rechazar'),
  reviewer: text('Last reviewer', '最近审核人', '最近審核人', 'Último revisor'),
  history: text('Versions and review history', '版本与审核记录', '版本與審核紀錄', 'Versiones e historial'),
  imported: text('Imported legacy record', '历史导入记录', '歷史匯入紀錄', 'Registro histórico importado'),
  city: text('City', '城市', '城市', 'Ciudad'),
  district: text('District', '城区', '城區', 'Distrito'),
  citywide: text('Citywide', '全城', '全城', 'Toda la ciudad'),
  aspect: text('Image aspect', '图片比例', '圖片比例', 'Proporción de imagen'),
  enabled: text('Enabled', '已启用', '已啟用', 'Habilitado'),
  savePlacement: text('Save placement', '保存广告位', '儲存廣告位', 'Guardar ubicación'),
  refresh: text('Refresh', '刷新', '重新整理', 'Actualizar'),
  previous: text('Previous', '上一页', '上一頁', 'Anterior'),
  next: text('Next', '下一页', '下一頁', 'Siguiente'),
  empty: text(
    'No records on this page.',
    '此页暂无记录。',
    '此頁暫無紀錄。',
    'No hay registros en esta página.',
  ),
  saved: text(
    'Saved. Review status was refreshed from the server.',
    '已保存，审核状态已从服务器刷新。',
    '已儲存，審核狀態已從伺服器重新整理。',
    'Guardado. Estado actualizado desde el servidor.',
  ),
  loading: text('Loading business records…', '正在加载业务记录…', '正在載入業務紀錄…', 'Cargando registros…'),
  stale: text(
    'This record changed. Refresh and reopen its editor before reviewing.',
    '记录已变更，请刷新并重新打开编辑器再审核。',
    '紀錄已變更，請重新整理並重開編輯器再審核。',
    'El registro cambió. Actualiza y vuelve a abrirlo antes de revisar.',
  ),
  sponsorRequired: text(
    'Complete and approve the current sponsor record first.',
    '请先补全并审核当前赞助商记录。',
    '請先補齊並審核目前贊助商紀錄。',
    'Completa y aprueba primero el registro actual del patrocinador.',
  ),
  placementDisabled: text(
    'This placement is disabled.',
    '该广告位已停用。',
    '該廣告位已停用。',
    'Esta ubicación está deshabilitada.',
  ),
  scheduleConflict: text(
    'Another approved campaign occupies this placement during that time.',
    '该时段已有已批准的同位投放。',
    '該時段已有已批准的同位投放。',
    'Otra campaña aprobada ocupa esa ubicación en ese período.',
  ),
};
type Tab = 'sponsors' | 'campaigns' | 'placements';
type Campaign = AdBusinessOverview['campaigns'][number];
type Sponsor = AdBusinessOverview['sponsors'][number];
type Placement = AdBusinessOverview['placements'][number];
type SponsorForm = {
  id?: string;
  expectedRevision?: number;
  companyName: string;
  displayName: string;
  contactName: string;
  contactEmail: string;
  contractNotes: string;
  industry: string;
};
export type AdCampaignForm = {
  id?: string;
  expectedRevision?: number;
  sponsorId: string;
  placement: 'subway' | 'cafe' | 'journal' | 'community';
  title: string;
  body: string;
  url: string;
  startsAt: string;
  endsAt: string;
  originalStartsAt?: string;
  originalEndsAt?: string;
  originalStartsInput?: string;
  originalEndsInput?: string;
  assetId: string;
  assetAlt: string;
  projectId: string;
};
const blankSponsor = (): SponsorForm => ({
  companyName: '',
  displayName: '',
  contactName: '',
  contactEmail: '',
  contractNotes: '',
  industry: '',
});
const utcInput = (value: string | number) => new Date(value).toISOString().slice(0, 19);
const blankCampaign = (): AdCampaignForm => ({
  sponsorId: '',
  placement: 'cafe',
  title: '',
  body: '',
  url: '',
  startsAt: utcInput(Date.now()),
  endsAt: utcInput(Date.now() + 86400000),
  assetId: '',
  assetAlt: '',
  projectId: '',
});
export function adCampaignSavePayload(form: AdCampaignForm, submit: boolean) {
  return {
    ...(form.id ? { expectedRevision: form.expectedRevision } : {}),
    sponsorId: form.sponsorId,
    placement: form.placement,
    title: form.title,
    body: form.body,
    url: form.url,
    startsAt:
      form.originalStartsAt && form.startsAt === form.originalStartsInput
        ? form.originalStartsAt
        : new Date(`${form.startsAt}Z`).toISOString(),
    endsAt:
      form.originalEndsAt && form.endsAt === form.originalEndsInput
        ? form.originalEndsAt
        : new Date(`${form.endsAt}Z`).toISOString(),
    assetId: form.assetId || null,
    assetAlt: form.assetId ? form.assetAlt : '',
    projectId: form.placement === 'community' ? form.projectId || null : null,
    submit,
  };
}
function localizedError(locale: Locale, e: any) {
  const code = e?.code;
  if (code === 'AD_VERSION_CONFLICT') return copy.stale[locale];
  if (code === 'AD_SPONSOR_REVIEW') return copy.sponsorRequired[locale];
  if (code === 'AD_PLACEMENT_DISABLED') return copy.placementDisabled[locale];
  if (code === 'AD_SCHEDULE_CONFLICT') return copy.scheduleConflict[locale];
  if (code === 'AD_PROJECT') return copy.projectInfo[locale];
  if (code === 'AD_ASSET_ALT') return copy.alt[locale];
  return errorText(locale, e);
}
function statusLabel(status: string, locale: Locale) {
  return status in copy ? copy[status as keyof typeof copy][locale] : status;
}
function Page({
  offset,
  total,
  limit,
  locale,
  onPage,
}: {
  offset: number;
  total: number;
  limit: number;
  locale: Locale;
  onPage: (n: number) => void;
}) {
  return (
    <div className="button-row">
      <button disabled={!offset} onClick={() => onPage(Math.max(0, offset - limit))}>
        {copy.previous[locale]}
      </button>
      <small>
        {Math.floor(offset / limit) + 1} / {Math.max(1, Math.ceil(total / limit))} · {total}
      </small>
      <button disabled={offset + limit >= total} onClick={() => onPage(offset + limit)}>
        {copy.next[locale]}
      </button>
    </div>
  );
}
export function AdReviewControls({
  locale,
  revision,
  busy,
  onReview,
}: {
  locale: Locale;
  revision: number;
  busy: boolean;
  onReview: (status: 'approved' | 'paused' | 'rejected', note: string, expectedRevision: number) => void;
}) {
  const [note, setNote] = useState('');
  return (
    <div>
      <label>
        {copy.reviewNote[locale]}
        <textarea
          aria-label={copy.reviewNote[locale]}
          minLength={3}
          maxLength={1000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className="button-row">
        {(['approved', 'paused', 'rejected'] as const).map((status) => (
          <button
            key={status}
            disabled={busy || note.trim().length < 3}
            data-review-revision={revision}
            onClick={() => onReview(status, note, revision)}
          >
            {copy[status === 'approved' ? 'approve' : status === 'paused' ? 'pause' : 'reject'][locale]}
          </button>
        ))}
      </div>
    </div>
  );
}
export function AdCampaignReviewCard({
  campaign,
  locale,
  busy,
  onEdit,
  onReview,
  onMessage,
}: {
  campaign: Campaign;
  locale: Locale;
  busy: boolean;
  onEdit: (campaign: Campaign) => void;
  onReview: (id: string, status: 'approved' | 'paused' | 'rejected', note: string, revision: number) => void;
  onMessage: (message: string, error?: boolean) => void;
}) {
  const [history, setHistory] = useState<any>(),
    [loading, setLoading] = useState(false);
  return (
    <article className="admin-report">
      <strong>{campaign.title}</strong>
      <small>
        {campaign.sponsorName} · {campaign.placement} · {copy.revision[locale]} {campaign.currentRevision} ·{' '}
        {statusLabel(campaign.status, locale)}
        {campaign.endsAt && Date.parse(campaign.endsAt) <= Date.now() ? ` · ${copy.expired[locale]}` : ''}
      </small>
      {campaign.legacyImported && <small>{copy.imported[locale]}</small>}
      <p>{campaign.body}</p>
      <p className="tiny">{campaign.url}</p>
      <small>
        {campaign.startsAt} → {campaign.endsAt} · UTC
      </small>
      {campaign.assetId && (
        <img
          loading="lazy"
          style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain' }}
          src={`/api/admin/ad-assets/${encodeURIComponent(campaign.assetId)}`}
          alt={campaign.assetAlt}
        />
      )}
      <small>{campaign.creativeHash}</small>
      {campaign.projectId && (
        <small>
          {copy.project[locale]}: {campaign.projectId}
        </small>
      )}
      {campaign.reviewedAt && (
        <small>
          {copy.reviewer[locale]}: {campaign.reviewedBy} · {campaign.reviewedAt} · {campaign.reviewNote}
        </small>
      )}
      <button disabled={busy} onClick={() => onEdit(campaign)}>
        {copy.edit[locale]}
      </button>
      <AdReviewControls
        key={`${campaign.id}:${campaign.currentRevision}`}
        locale={locale}
        revision={campaign.currentRevision}
        busy={busy}
        onReview={(status, note, revision) => onReview(campaign.id, status, note, revision)}
      />
      <details
        onToggle={async (e) => {
          if (!e.currentTarget.open || history || loading) return;
          setLoading(true);
          try {
            setHistory(await api(`/admin/ads/business/campaigns/${campaign.id}/history`));
          } catch (error) {
            onMessage(localizedError(locale, error), true);
          } finally {
            setLoading(false);
          }
        }}
      >
        <summary>{copy.history[locale]}</summary>
        {loading && <p>{copy.loading[locale]}</p>}
        {history && (
          <>
            <ul>
              {history.revisions.map((r: any) => (
                <li key={r.revision}>
                  {copy.revision[locale]} {r.revision} · {r.title}
                  <br />
                  <small>
                    {r.body} · {r.url} · {r.creativeHash} · {r.createdAt}
                  </small>
                </li>
              ))}
            </ul>
            <ul>
              {history.reviews.map((r: any, i: number) => (
                <li key={i}>
                  {copy.revision[locale]} {r.revision} · {statusLabel(r.result, locale)} · {r.reviewedBy} ·{' '}
                  {r.reviewedAt}
                  <p>{r.note}</p>
                </li>
              ))}
            </ul>
          </>
        )}
      </details>
    </article>
  );
}
function PlacementEditor({
  row,
  locale,
  busy,
  onSave,
}: {
  row: Placement;
  locale: Locale;
  busy: boolean;
  onSave: (id: string, input: any) => void;
}) {
  const [district, setDistrict] = useState(row.district || ''),
    [aspect, setAspect] = useState(row.aspect),
    [enabled, setEnabled] = useState(row.enabled);
  return (
    <article className="admin-report">
      <strong>
        {row.id} · {copy.revision[locale]} {row.revision}
      </strong>
      <small>
        {copy.city[locale]}: {row.cityId}
      </small>
      <label>
        {copy.district[locale]}
        <select value={district} onChange={(e) => setDistrict(e.target.value)}>
          <option value="">{copy.citywide[locale]}</option>
          {zones.map((zone) => (
            <option key={zone.id} value={zone.id}>
              {zone.name[locale]}
            </option>
          ))}
        </select>
      </label>
      <label>
        {copy.aspect[locale]}
        <select value={aspect} onChange={(e) => setAspect(e.target.value)}>
          {['16:9', '4:3', '1:1'].map((v) => (
            <option key={v}>{v}</option>
          ))}
        </select>
      </label>
      <label>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        {copy.enabled[locale]}
      </label>
      <button
        disabled={busy}
        onClick={() =>
          onSave(row.id, { expectedRevision: row.revision, district: district || null, aspect, enabled })
        }
      >
        {copy.savePlacement[locale]}
      </button>
    </article>
  );
}

export default function AdBusinessPanel({
  locale,
  onMessage,
  onChanged,
  initialData,
  initialTab = 'sponsors',
}: {
  locale: Locale;
  onMessage: (message: string, error?: boolean) => void;
  onChanged?: () => void | Promise<void>;
  initialData?: AdBusinessOverview;
  initialTab?: Tab;
}) {
  const [tab, setTab] = useState<Tab>(initialTab),
    [data, setData] = useState(initialData),
    [busy, setBusy] = useState(false),
    [campaignOffset, setCampaignOffset] = useState(0),
    [sponsorOffset, setSponsorOffset] = useState(0),
    [sponsor, setSponsor] = useState<SponsorForm>(blankSponsor),
    [campaign, setCampaign] = useState<AdCampaignForm>(blankCampaign),
    [campaignEditorKey, setCampaignEditorKey] = useState('draft:0');
  const editorSequence = useRef(0);
  const currentEditor = useRef({ key: campaignEditorKey, assetId: campaign.assetId });
  currentEditor.current = { key: campaignEditorKey, assetId: campaign.assetId };
  const replaceCampaign = (next: AdCampaignForm) => {
    const key = `${next.id || 'draft'}:${next.expectedRevision || 0}:${++editorSequence.current}`;
    currentEditor.current = { key, assetId: next.assetId };
    setCampaignEditorKey(key);
    setCampaign(next);
  };
  const changeAsset = (assetId: string) => {
    currentEditor.current = { ...currentEditor.current, assetId };
    setCampaign((old) => ({ ...old, assetId, ...(!assetId ? { assetAlt: '' } : {}) }));
  };
  const resetCampaign = (key: string) => {
    if (currentEditor.current.key === key) replaceCampaign(blankCampaign());
  };
  const t = (key: keyof typeof copy) => copy[key][locale];
  const refresh = async () => {
    setData(await api(`/admin/ads/business?campaignOffset=${campaignOffset}&sponsorOffset=${sponsorOffset}`));
  };
  useEffect(() => {
    void refresh().catch((error) => onMessage(localizedError(locale, error), true));
  }, [campaignOffset, sponsorOffset, locale]);
  const action = async (path: string, payload: unknown, done?: () => void) => {
    setBusy(true);
    try {
      await post(`/admin/ads/business${path}`, payload);
      done?.();
      await refresh();
      await onChanged?.();
      window.dispatchEvent(new Event('ado-ads-changed'));
      onMessage(t('saved'));
    } catch (error) {
      onMessage(localizedError(locale, error), true);
    } finally {
      setBusy(false);
    }
  };
  const sponsorField = (
    key: keyof Omit<SponsorForm, 'id' | 'expectedRevision'>,
    label: keyof typeof copy,
    maxLength: number,
    required = true,
  ) => (
    <label key={key}>
      {t(label)}
      <input
        type={key === 'contactEmail' ? 'email' : 'text'}
        required={required}
        maxLength={maxLength}
        value={sponsor[key]}
        onChange={(e) => setSponsor({ ...sponsor, [key]: e.target.value })}
      />
    </label>
  );
  const campaignField = (
    key: 'title' | 'url' | 'startsAt' | 'endsAt' | 'assetId' | 'assetAlt',
    label: keyof typeof copy,
    required = true,
  ) => (
    <label key={key}>
      {t(label)}
      <input
        type={key === 'startsAt' || key === 'endsAt' ? 'datetime-local' : key === 'url' ? 'url' : 'text'}
        step={key === 'startsAt' || key === 'endsAt' ? 1 : undefined}
        required={required}
        maxLength={key === 'assetAlt' ? 300 : key === 'title' ? 80 : key === 'assetId' ? 36 : 4096}
        value={campaign[key]}
        onChange={(e) => {
          const value = e.target.value;
          if (key === 'assetId') changeAsset(value);
          else setCampaign((old) => ({ ...old, [key]: value }));
        }}
      />
    </label>
  );
  return (
    <section className="ad-business-panel">
      <h3>{t('title')}</h3>
      <p className="muted small">{t('private')}</p>
      <div className="social-tabs">
        {(['sponsors', 'campaigns', 'placements'] as const).map((key) => (
          <button
            key={key}
            className={tab === key ? 'active' : ''}
            onClick={() => {
              if (tab !== key) replaceCampaign(campaign);
              setTab(key);
            }}
          >
            {t(key)}
          </button>
        ))}
        <button
          disabled={busy}
          onClick={() => void refresh().catch((error) => onMessage(localizedError(locale, error), true))}
        >
          {t('refresh')}
        </button>
      </div>
      {!data && <p role="status">{t('loading')}</p>}
      {tab === 'sponsors' && (
        <>
          <form
            className="admin-campaign-form"
            onSubmit={(e) => {
              e.preventDefault();
              void action('/sponsors', sponsor, () => setSponsor(blankSponsor()));
            }}
          >
            <h4>
              {sponsor.id ? `${t('edit')} · ${t('revision')} ${sponsor.expectedRevision}` : t('newSponsor')}
            </h4>
            {sponsorField('companyName', 'company', 120)}
            {sponsorField('displayName', 'display', 60)}
            {sponsorField('contactName', 'contact', 120, false)}
            {sponsorField('contactEmail', 'email', 254, false)}
            {sponsorField('industry', 'industry', 80)}
            <label>
              {t('notes')}
              <textarea
                maxLength={4000}
                value={sponsor.contractNotes}
                onChange={(e) => setSponsor({ ...sponsor, contractNotes: e.target.value })}
              />
            </label>
            <button className="button primary" disabled={busy}>
              {t('saveSponsor')}
            </button>
            {sponsor.id && (
              <button type="button" onClick={() => setSponsor(blankSponsor())}>
                {t('cancel')}
              </button>
            )}
          </form>
          {data?.sponsors.map((row: Sponsor) => (
            <article className="admin-report" key={`${row.id}:${row.revision}`}>
              <strong>
                {row.companyName} · {row.displayName}
              </strong>
              <small>
                {t('revision')} {row.revision} · {statusLabel(row.status, locale)} · {row.industry}
              </small>
              <p>
                {row.contactName} · {row.contactEmail}
              </p>
              <p>{row.contractNotes}</p>
              {row.legacyImported && <small>{t('imported')}</small>}
              <button
                disabled={busy}
                onClick={() =>
                  setSponsor({
                    id: row.id,
                    expectedRevision: row.revision,
                    companyName: row.companyName,
                    displayName: row.displayName,
                    contactName: row.contactName,
                    contactEmail: row.contactEmail,
                    contractNotes: row.contractNotes,
                    industry: row.industry,
                  })
                }
              >
                {t('edit')}
              </button>
              <AdReviewControls
                locale={locale}
                revision={row.revision}
                busy={busy}
                onReview={(status, note, expectedRevision) =>
                  void action(`/sponsors/${row.id}/review`, { status, note, expectedRevision })
                }
              />
            </article>
          ))}
          {data && (
            <>
              <p>{!data.sponsors.length && t('empty')}</p>
              <Page
                offset={data.sponsorOffset}
                total={data.sponsorTotal}
                limit={data.limit}
                locale={locale}
                onPage={setSponsorOffset}
              />
            </>
          )}
        </>
      )}
      {tab === 'campaigns' && (
        <>
          <p className="muted small">{t('approvalInfo')}</p>
          <form
            className="admin-campaign-form"
            onSubmit={(e) => {
              e.preventDefault();
              try {
                void action(
                  campaign.id ? `/campaigns/${campaign.id}` : '/campaigns',
                  adCampaignSavePayload(campaign, true),
                  () => resetCampaign(campaignEditorKey),
                );
              } catch (error) {
                onMessage(localizedError(locale, error), true);
              }
            }}
          >
            <h4>
              {campaign.id
                ? `${t('edit')} · ${t('revision')} ${campaign.expectedRevision}`
                : t('newCampaign')}
            </h4>
            <label>
              {t('sponsor')}
              <select
                required
                value={campaign.sponsorId}
                onChange={(e) => setCampaign({ ...campaign, sponsorId: e.target.value })}
              >
                <option value="">{t('choose')}</option>
                {campaign.sponsorId && !data?.sponsors.some((s) => s.id === campaign.sponsorId) && (
                  <option value={campaign.sponsorId}>{campaign.sponsorId}</option>
                )}
                {data?.sponsors.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.displayName} · {statusLabel(row.status, locale)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('placement')}
              <select
                value={campaign.placement}
                onChange={(e) =>
                  setCampaign({
                    ...campaign,
                    placement: e.target.value as AdCampaignForm['placement'],
                    projectId: '',
                  })
                }
              >
                {['subway', 'cafe', 'journal', 'community'].map((id) => (
                  <option key={id}>{id}</option>
                ))}
              </select>
            </label>
            {campaignField('title', 'headline')}
            <label>
              {t('body')}
              <textarea
                maxLength={180}
                value={campaign.body}
                onChange={(e) => setCampaign({ ...campaign, body: e.target.value })}
              />
            </label>
            {campaignField('url', 'url')}
            {campaignField('startsAt', 'starts')}
            {campaignField('endsAt', 'ends')}
            <h4>{t('image')}</h4>
            <p className="muted small">{t('imageInfo')}</p>
            <AdAssetUpload
              key={campaignEditorKey}
              locale={locale}
              selectedAssetId={campaign.assetId || null}
              selectionKey={campaignEditorKey}
              onMessage={onMessage}
              onUploaded={(asset) => {
                if (
                  currentEditor.current.key !== campaignEditorKey ||
                  currentEditor.current.assetId !== campaign.assetId
                )
                  return false;
                currentEditor.current = { key: campaignEditorKey, assetId: asset.id };
                setCampaign((old) => ({ ...old, assetId: asset.id }));
                return true;
              }}
            />
            {campaignField('assetId', 'assetId', false)}
            {campaignField('assetAlt', 'alt', Boolean(campaign.assetId))}
            {campaign.assetId && (
              <button type="button" onClick={() => changeAsset('')}>
                {t('removeImage')}
              </button>
            )}
            {campaign.placement === 'community' && (
              <>
                <p className="muted small">{t('projectInfo')}</p>
                <label>
                  {t('project')}
                  <select
                    required
                    value={campaign.projectId}
                    onChange={(e) => setCampaign({ ...campaign, projectId: e.target.value })}
                  >
                    <option value="">{t('choose')}</option>
                    {campaign.projectId && !data?.projects.some((p) => p.id === campaign.projectId) && (
                      <option value={campaign.projectId}>{campaign.projectId}</option>
                    )}
                    {data?.projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title} · {p.startsAt?.slice(0, 10)}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
            <div className="button-row">
              <button className="button primary" disabled={busy}>
                {t('submit')}
              </button>
              <button
                type="button"
                disabled={
                  busy ||
                  !campaign.sponsorId ||
                  !campaign.title ||
                  !campaign.url ||
                  Boolean(campaign.assetId && !campaign.assetAlt.trim())
                }
                onClick={() => {
                  try {
                    void action(
                      campaign.id ? `/campaigns/${campaign.id}` : '/campaigns',
                      adCampaignSavePayload(campaign, false),
                      () => resetCampaign(campaignEditorKey),
                    );
                  } catch (error) {
                    onMessage(localizedError(locale, error), true);
                  }
                }}
              >
                {t('saveDraft')}
              </button>
              {campaign.id && (
                <button type="button" onClick={() => replaceCampaign(blankCampaign())}>
                  {t('cancel')}
                </button>
              )}
            </div>
          </form>
          {data?.campaigns.map((row) => (
            <AdCampaignReviewCard
              key={`${row.id}:${row.currentRevision}`}
              campaign={row}
              locale={locale}
              busy={busy}
              onMessage={onMessage}
              onEdit={(c) =>
                replaceCampaign({
                  id: c.id,
                  expectedRevision: c.currentRevision,
                  sponsorId: c.sponsorId,
                  placement: c.placement,
                  title: c.title,
                  body: c.body,
                  url: c.url || '',
                  startsAt: utcInput(c.startsAt!),
                  endsAt: utcInput(c.endsAt!),
                  originalStartsAt: c.startsAt!,
                  originalEndsAt: c.endsAt!,
                  originalStartsInput: utcInput(c.startsAt!),
                  originalEndsInput: utcInput(c.endsAt!),
                  assetId: c.assetId || '',
                  assetAlt: c.assetAlt || '',
                  projectId: c.projectId || '',
                })
              }
              onReview={(id, status, note, expectedRevision) =>
                void action(`/campaigns/${id}/review`, { status, note, expectedRevision })
              }
            />
          ))}
          {data && (
            <>
              <p>{!data.campaigns.length && t('empty')}</p>
              <Page
                offset={data.campaignOffset}
                total={data.campaignTotal}
                limit={data.limit}
                locale={locale}
                onPage={setCampaignOffset}
              />
            </>
          )}
        </>
      )}
      {tab === 'placements' &&
        data?.placements.map((row) => (
          <PlacementEditor
            key={`${row.id}:${row.revision}`}
            row={row}
            locale={locale}
            busy={busy}
            onSave={(id, input) => void action(`/placements/${id}`, input)}
          />
        ))}
    </section>
  );
}
